import React, { useState, useEffect } from 'react'
import injectStyles from 'react-jss'

import SearchBox from '../../FilterAndPaginationComponent/SearchBox'
import { debounce } from '../../../Utils/Functions'
import { searchPostcodesPFGQL } from '../../../Module/graphQL/PathFinder/pathFinderGQL'
import { normalizeGraphQLConnection } from '../../../shared/graphql/utils'
import styles from '../styles'
import {
  getOverlayColorAndOpacity,
  getOverlayPath,
} from '../../../Containers/BuilderMap/util'

/**
 * Wrapper around searchPostcodesPFGQL that handles errors and basic formatting
 * @param {object} variables GQL query variables
 * @returns {array} Postcodes
 */
const searchPostcodesPFGQLWrapper = async variables => {
  const response = await searchPostcodesPFGQL(variables)
  const { data, errors } = await response.json()

  if (errors && errors.length) {
    throw data.errors[0].message
  }
  return normalizeGraphQLConnection(data.searchPostcodes)
}

/**
 * Component with the autosuggestive search for postcodes and multi-select for choosing them
 * @param {string} props.countryCode Country code
 * @param {function} props.onUpdate To notify parent about any new active tags ot if any were deleted
 * @param {object} props.classes styles
 *
 * @returns {JSX.Element} <PostcodesSearch />
 */

export const formatFeaturesToOverlays = rawFeatures => {
  return rawFeatures.map(feature => {
    const { name, customToggled = false } = feature.properties

    const { color, fillOpacity, strokeOpacity } = getOverlayColorAndOpacity({
      isEdit: false,
      isPostcode: true,
    })

    const paths = getOverlayPath({
      coords: feature.geometry.coordinates,
      isMultiPolygon: feature.geometry.type == 'MultiPolygon',
    })

    return {
      id: name,
      paths,
      color,
      name,
      metadata: feature.properties,
      mapPolygonProps: {
        key: name,
        editingBlocked: true,
        canBeDeleted: false,
        fillOpacity,
        strokeOpacity,
        customToggled,
        payload: {
          label: name,
        },
        zIndex: 0,
      },
      isPostcode: true,
    }
  })
}
const PostcodesSearch = ({ countryCode, onUpdate, classes }) => {
  const [postcodes, setPostcodes] = useState([])
  const [activePostcodes, setActivePostcodes] = useState([])
  const [suggestions, setSuggestions] = useState([])

  /**
   * Will call onUpdate if activeTags were changed
   */
  useEffect(() => {
    onUpdate({ activePostcodes })
  }, [activePostcodes.length])

  /**
   * Sends request to the PF with searchedString and sets result as suggestions
   * Debounce 1s to prevent spam
   * @param {object} searchString Searched postcode name
   */
  const onSearch = debounce(async searchString => {
    const data = await searchPostcodesPFGQLWrapper({
      searchString,
      countryCode,
      first: 10,
    })

    const options = {
      isPostcode: 'Postcodes',
      isIRIS: false,
    }

    const postcodes = formatFeaturesToOverlays(data, options)
    setPostcodes(postcodes)
    const suggestions = postcodes.map(item => item.name)
    setSuggestions(suggestions)
  }, 400)

  /**
   * Add tag
   */
  const onAddTag = tag => {
    const postcode = postcodes.find(item => item.name === tag)

    if (postcode) {
      setActivePostcodes(prev => [...prev, postcode])
    }
  }

  /**
   * Delete selected tag
   */
  const onRemoveTag = tag => {
    setActivePostcodes(activePostcodes =>
      activePostcodes.filter(item => item.name !== tag),
    )
  }

  return (
    <div className={classes.wrapper}>
      <SearchBox
        className={classes.searchBox}
        onChange={onSearch}
        handleInputChange={onSearch}
        suggestions={suggestions}
        tags={activePostcodes.map(item => item.name)}
        onAddTag={onAddTag}
        onRemoveTag={onRemoveTag}
        placeholder={'Search for postcodes'}
      />
    </div>
  )
}

export default injectStyles(styles)(PostcodesSearch)
